import React, { useState } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';

const CATEGORIES = [
  { val:'MACHINE_FUEL',   label:'Machine Fuel',                 icon:'⛽' },
  { val:'DELIVERY',       label:'Delivery Expenses',            icon:'🚛' },
  { val:'PURCHASE',       label:'Purchase / Materials',         icon:'🛒' },
  { val:'SALARY',         label:'Salary / Wages',               icon:'💰' },
  { val:'EMPLOYEE',       label:'Employee Expenses',            icon:'👷' },
  { val:'FOOD',           label:'Food Expenses',                icon:'🍱' },
  { val:'TRAVEL',         label:'Travel Expenses',              icon:'✈️' },
  { val:'ELECTRICITY',    label:'Electricity Bill',             icon:'⚡' },
  { val:'GENERATOR_FUEL', label:'Generator Fuel',               icon:'🔋' },
  { val:'MAINTENANCE',    label:'Machine Maintenance / Repair', icon:'🔧' },
  { val:'RENT',           label:'Rent',                         icon:'🏢' },
  { val:'COMMUNICATION',  label:'Communication',                icon:'📱' },
  { val:'OFFICE',         label:'Office Expenses',              icon:'📋' },
  { val:'VEHICLE_MAINT',  label:'Vehicle Maintenance',          icon:'🚗' },
  { val:'MEDICAL_SAFETY', label:'Medical / Safety',             icon:'💊' },
  { val:'OTHER',          label:'Other Expenses',               icon:'📌' },
];

const inr = v => `₹${Number(v||0).toLocaleString('en-IN',{minimumFractionDigits:2})}`;
const today = new Date().toISOString().split('T')[0];
const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
const fmt = d => { try{ return new Date(d).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'}); }catch{ return d; }};

const getPreset = preset => {
  const now = new Date();
  const iso = d => d.toISOString().split('T')[0];
  if(preset==='today')      return { from:iso(now), to:iso(now) };
  if(preset==='this_month') return { from:`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-01`, to:iso(now) };
  if(preset==='last_month') {
    const y = now.getMonth()===0 ? now.getFullYear()-1 : now.getFullYear();
    const m = now.getMonth()===0 ? 12 : now.getMonth();
    const lastDay = new Date(y, m, 0).getDate();
    return { from:`${y}-${String(m).padStart(2,'0')}-01`, to:`${y}-${String(m).padStart(2,'0')}-${lastDay}` };
  }
  if(preset==='this_year')  return { from:`${now.getFullYear()}-01-01`, to:iso(now) };
  return { from:iso(now), to:iso(now) };
};

// ─── Company Header ───────────────────────────────────────────────────────────
const CompanyHeader = ({ reportTitle, fromDate, toDate, reportNumber }) => (
  <div style={{ borderBottom:'3px solid #1e3a5f', paddingBottom:16, marginBottom:20 }}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
      <div style={{ display:'flex', alignItems:'center', gap:16 }}>
        {/* Logo  */}
        <img src="/TycheAbodes-logo1.png" alt="Company Logo" style={{ width:70, height:70, objectFit:'contain', borderRadius:8 }}/>
        <div>
          <div style={{ fontSize:18, fontWeight:800, color:'#1e3a5f', fontFamily:'Inter,sans-serif', letterSpacing:0.5 }}>TYCHE ABODES PRIVATE LIMITED</div>
          <div style={{ fontSize:11, color:'#555', marginTop:3 }}>No. 401, Ramani's Frazer Square, Near Head Post Office,</div>
          <div style={{ fontSize:11, color:'#555' }}>Goods Shed Road, Coimbatore - 641001.</div>
          <div style={{ fontSize:11, color:'#555', marginTop:2 }}>📞 +91 94433 23246 &nbsp;|&nbsp; GSTIN: 33AAKCT5967G1ZY</div>
        </div>
      </div>
      <div className="expense-report-meta" style={{ textAlign:'right', flexShrink:0 }}>
        <div style={{ fontSize:16, fontWeight:800, color:'#1e3a5f', textTransform:'uppercase', letterSpacing:1, fontFamily:'Inter,sans-serif' }}>{reportTitle}</div>
        {reportNumber&&<div style={{ fontSize:12, color:'#555', marginTop:4 }}>Report No: <strong>{reportNumber}</strong></div>}
        <div style={{ fontSize:12, color:'#555', marginTop:2 }}>Period: <strong>{fmt(fromDate)} — {fmt(toDate)}</strong></div>
        <div style={{ fontSize:12, color:'#555', marginTop:2 }}>Generated: <strong>{new Date().toLocaleDateString('en-IN')}</strong></div>
      </div>
    </div>
  </div>
);

export default function ExpenseReportPage() {
  const [filters, setFilters] = useState({ from:firstOfMonth, to:today });
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const setF = (k,v) => setFilters(f=>({...f,[k]:v}));

  const generate = async () => {
    setLoading(true);
    try {
      const [expRes, salesRes] = await Promise.all([
        api.get(`/expenses/?from_date=${filters.from}&to_date=${filters.to}&page_size=500&ordering=date`),
        api.get(`/sales/orders/?ordering=-order_date&page_size=500`),
      ]);
      const expenses  = expRes.data.results||[];
      const allOrders = salesRes.data.results||[];
      const orders    = allOrders.filter(o => o.order_date >= filters.from && o.order_date <= filters.to);
      const totalRevenue  = orders.reduce((s,o)=>s+(parseFloat(o.total_amount)||0),0);
      const totalPaid     = orders.reduce((s,o)=>s+(parseFloat(o.paid_amount)||0),0);
      const byCategory = {};
      expenses.forEach(e=>{
        if(!byCategory[e.category]) byCategory[e.category] = { total:0, count:0, items:[] };
        byCategory[e.category].total += parseFloat(e.amount||0);
        byCategory[e.category].count++;
        byCategory[e.category].items.push(e);
      });
      const totalExpenses = expenses.reduce((s,e)=>s+(parseFloat(e.amount)||0),0);
      const netProfit     = totalRevenue - totalExpenses;
      const now = new Date();
      const reportNumber = `EXP/${now.getFullYear()}/${String(now.getMonth()+1).padStart(2,'0')}/${String(now.getDate()).padStart(2,'0')}`;
      setData({ expenses, orders, byCategory, totalRevenue, totalPaid, totalExpenses, netProfit, reportNumber });
    } finally { setLoading(false); }
  };

  const exportCSV = () => {
    if(!data) return;
    const rows = [['Date','Expense #','Category','Vendor','Payment Mode','Notes','Amount']];
    data.expenses.forEach(e=>{
      const cat = CATEGORIES.find(c=>c.val===e.category);
      rows.push([e.date, e.expense_number, cat?.label||e.category, e.vendor_name||'—', e.payment_mode, `"${(e.notes||'').replace(/"/g,'""')}"`, `"${inr(e.amount)}"`]);
    });
    rows.push(['','','','','','TOTAL',data.totalExpenses.toFixed(2)]);
    const csv = rows.map(r=>r.join(',')).join('\n');
    const blob = new Blob([csv],{type:'text/csv'});
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `expense_report_${filters.from}_to_${filters.to}.csv`;
    a.click();
  };

  return (
    <Layout title="Expense Report" subtitle="Expenses · Revenue · Profit / Loss">
      <style>{`
      @media print {
      .no-print { display: none !important; }
      .main-content { margin-left: 0 !important; }
      .sidebar, .topbar, .hamburger { display: none !important; }
      .page-content { padding: 0 !important; }
      table { font-size: 8px !important; }
      td, th { padding: 3px 5px !important; }
      @page { margin: 8mm; size: A4; }
    }
  `}</style>

      {/* Filters */}
      <div className="card no-print" style={{ marginBottom:16 }}>
        <div style={{ fontSize:11, fontWeight:700, letterSpacing:'1.2px', textTransform:'uppercase', color:'var(--brand)', borderBottom:'2px solid var(--border)', paddingBottom:8, marginBottom:14 }}>Date Range</div>
        <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
          {[['today','Today'],['this_month','This Month'],['last_month','Last Month'],['this_year','This Year']].map(([p,l])=>(
            <button key={p} type="button" onClick={()=>setFilters(getPreset(p))}
              style={{ padding:'5px 12px', fontSize:12, borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-input)', color:'var(--text-secondary)', cursor:'pointer', fontWeight:500 }}>
              {l}
            </button>
          ))}
        </div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:12, alignItems:'end' }}>
          <div className="form-group" style={{ flex:'1 1 160px' }}>
            <label className="form-label">From Date</label>
            <input className="form-input" type="date" value={filters.from} onChange={e=>setF('from',e.target.value)} max={today}/>
          </div>
          <div className="form-group" style={{ flex:'1 1 160px' }}>
            <label className="form-label">To Date</label>
            <input className="form-input" type="date" value={filters.to} onChange={e=>setF('to',e.target.value)} max={today}/>
          </div>
          <div className="form-group" style={{ flex:'0 0 auto' }}>
            <label className="form-label" style={{ opacity:0 }}>x</label>
            <button className="btn btn-primary" onClick={generate} disabled={loading}>{loading?'Loading...':'Generate'}</button>
          </div>
        </div>
      </div>

      {data && (
        <div>
          {/* Print button */}
          <div className="no-print" style={{ display:'flex', justifyContent:'flex-end', marginBottom:16, gap:8 }}>
            <button className="btn btn-secondary btn-sm" onClick={()=>{
              sessionStorage.setItem('printExpenseReport', JSON.stringify({
                expenses: data.expenses,
                totalExpenses: data.totalExpenses,
                totalRevenue: data.totalRevenue,
                totalPaid: data.totalPaid,
                netProfit: data.netProfit,
                byCategory: data.byCategory,
                filters,
                reportNumber: data.reportNumber
              }));
              setTimeout(()=>{ window.open('/print/expense-report', '_blank'); }, 100);
            }}>🖨 Print / Save as PDF</button>
            <button className="btn btn-secondary btn-sm" onClick={exportCSV}>📊 Export CSV</button>
          </div>

          {/* ── PRINTABLE REPORT ── */}
          <div style={{ background:'#fff', padding:24, border:'1px solid #ddd', borderRadius:8 }}>

            {/* Company Header */}
            <CompanyHeader
              reportTitle="Expense Report"
              fromDate={filters.from}
              toDate={filters.to}
              reportNumber={data.reportNumber}
            />

            {/* Profit/Loss Summary */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:0, border:'1px solid #ddd', borderRadius:8, overflow:'hidden', marginBottom:20 }}>
              <div style={{ padding:16, textAlign:'center', background:'#f0fdf4', borderRight:'1px solid #ddd' }}>
                <div style={{ fontSize:10, color:'#555', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:6 }}>Total Revenue</div>
                <div style={{ fontFamily:'DM Mono,monospace', fontSize:24, fontWeight:700, color:'#3B6D11' }}>{inr(data.totalRevenue)}</div>
                <div style={{ fontSize:11, color:'#888', marginTop:3 }}>Paid: {inr(data.totalPaid)}</div>
              </div>
              <div style={{ padding:16, textAlign:'center', background:'#fef2f2', borderRight:'1px solid #ddd' }}>
                <div style={{ fontSize:10, color:'#555', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:6 }}>Total Expenses</div>
                <div style={{ fontFamily:'DM Mono,monospace', fontSize:24, fontWeight:700, color:'#C0392B' }}>{inr(data.totalExpenses)}</div>
                <div style={{ fontSize:11, color:'#888', marginTop:3 }}>{data.expenses.length} records</div>
              </div>
              <div style={{ padding:16, textAlign:'center', background:data.netProfit>=0?'#f0fdf4':'#fef2f2' }}>
                <div style={{ fontSize:10, color:'#555', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:6 }}>{data.netProfit>=0?'Net Profit':'Net Loss'}</div>
                <div style={{ fontFamily:'DM Mono,monospace', fontSize:28, fontWeight:700, color:data.netProfit>=0?'#3B6D11':'#C0392B' }}>
                  {data.netProfit>=0?'+':''}{inr(data.netProfit)}
                </div>
                <div style={{ fontSize:12, color:'#555', marginTop:4, fontWeight:700 }}>
                  {data.netProfit>=0?'✅ PROFIT':'❌ LOSS'}
                </div>
              </div>
            </div>

            {/* Expense by Category */}
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#1e3a5f', textTransform:'uppercase', letterSpacing:'1px', borderBottom:'2px solid #1e3a5f', paddingBottom:6, marginBottom:12 }}>Expenses by Category</div>
              <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr style={{ background:'#1e3a5f', color:'#fff' }}>
                    <th style={{ padding:'8px 12px', textAlign:'left', fontWeight:600 }}>Category</th>
                    <th style={{ padding:'8px 12px', textAlign:'center', fontWeight:600 }}>Records</th>
                    <th style={{ padding:'8px 12px', textAlign:'right', fontWeight:600 }}>Amount</th>
                    <th style={{ padding:'8px 12px', textAlign:'right', fontWeight:600 }}>% of Total</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(data.byCategory)
                    .sort((a,b)=>b[1].total-a[1].total)
                    .map(([cat,info],i)=>{
                      const catInfo = CATEGORIES.find(c=>c.val===cat);
                      const pct = data.totalExpenses>0?(info.total/data.totalExpenses*100).toFixed(1):0;
                      return (
                        <tr key={i} style={{ background:i%2===0?'#f8f9fc':'#fff', borderBottom:'1px solid #eee' }}>
                          <td style={{ padding:'8px 12px', fontWeight:500 }}>{catInfo?.icon} {catInfo?.label||cat}</td>
                          <td style={{ padding:'8px 12px', textAlign:'center', fontFamily:'DM Mono,monospace' }}>{info.count}</td>
                          <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:'DM Mono,monospace', fontWeight:600, color:'#C0392B' }}>{inr(info.total)}</td>
                          <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:'DM Mono,monospace' }}>{pct}%</td>
                        </tr>
                      );
                    })}
                  <tr style={{ background:'#1e3a5f', color:'#fff', fontWeight:700 }}>
                    <td style={{ padding:'8px 12px' }}>TOTAL</td>
                    <td style={{ padding:'8px 12px', textAlign:'center', fontFamily:'DM Mono,monospace' }}>{data.expenses.length}</td>
                    <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:14 }}>{inr(data.totalExpenses)}</td>
                    <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:'DM Mono,monospace' }}>100%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

            {/* All Expense Records */}
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#1e3a5f', textTransform:'uppercase', letterSpacing:'1px', borderBottom:'2px solid #1e3a5f', paddingBottom:6, marginBottom:12 }}>Expense Records</div>
              {data.expenses.length===0 ? (
                <div style={{ textAlign:'center', padding:30, color:'#999' }}>No expenses found for this period.</div>
              ) : (
                <div style={{ overflowX:'auto' }}>
                <table style={{width:'100%', borderCollapse:'collapse', fontSize:10}}>
                  <thead>
                    <tr style={{background:'#1e3a5f', color:'#fff'}}>
                      <th style={{padding:'6px 8px', textAlign:'left', fontWeight:600}}>Exp ID</th>
                      <th style={{padding:'6px 8px', textAlign:'left', fontWeight:600}}>Date</th>
                      <th style={{padding:'6px 8px', textAlign:'left', fontWeight:600}}>Category</th>
                      <th style={{padding:'6px 8px', textAlign:'left', fontWeight:600}}>Vendor</th>
                      <th style={{padding:'6px 8px', textAlign:'left', fontWeight:600}}>Payment</th>
                      <th style={{padding:'6px 8px', textAlign:'left', fontWeight:600}}>Notes</th>
                      <th style={{padding:'6px 8px', textAlign:'right', fontWeight:600}}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.expenses.map((e,i)=>{
                      const cat = CATEGORIES.find(c=>c.val===e.category);
                      return (
                      <tr key={i} style={{background:i%2===0?'#f8f9fc':'#fff', borderBottom:'1px solid #eee'}}>
                        <td style={{padding:'5px 8px', color:'#1e3a5f', fontWeight:600}}>{e.expense_number}</td>
                        <td style={{padding:'5px 8px'}}>{e.date}</td>
                        <td style={{padding:'5px 8px'}}>{cat?.label||e.category}</td>
                        <td style={{padding:'5px 8px'}}>{e.vendor_name||'—'}</td>
                        <td style={{padding:'5px 8px'}}>{e.payment_mode}</td>
                        <td style={{padding:'5px 8px'}}>{e.notes||'—'}</td>
                        <td style={{padding:'5px 8px', fontWeight:700, color:'#C0392B', textAlign:'right'}}>{inr(e.amount)}</td>
                      </tr>
                    );
                  })}
                  <tr style={{background:'#1e3a5f', color:'#fff', fontWeight:700}}>
                    <td colSpan={6} style={{padding:'7px 8px'}}>Total Expenses</td>
                    <td style={{padding:'7px 8px', textAlign:'right'}}>{inr(data.totalExpenses)}</td>
                    </tr>
                    </tbody>
                </table>
              </div>
              )}
            </div>

            {/* Report Footer */}
            <div style={{ borderTop:'2px solid #1e3a5f', paddingTop:12, marginTop:20, display:'flex', justifyContent:'space-between', fontSize:11, color:'#666' }}>
              <span>TYCHE ABODES PRIVATE LIMITED — Confidential</span>
              <span>Report: {data.reportNumber} | Generated: {new Date().toLocaleString('en-IN')}</span>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}